import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styles from './HomeOwnerNavbar.module.css';
import { sessionService } from '../../../services/api';

const HomeOwnerNavbar = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSubDropdown, setActiveSubDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Add isLoggedIn check
  const isLoggedIn = localStorage.getItem('token') !== null;
  const user = isLoggedIn ? JSON.parse(localStorage.getItem('user')) : null;

  const handleLogout = async () => {
    try {
      // Call our session service to properly end the session on the server
      await sessionService.logout();
    } catch (error) {
      console.error('Error during logout:', error);
      // Continue with logout even if server request fails
    } finally {
      // Always clear local storage and redirect
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/homeowner');
      window.location.reload();
    }
  };

  const handleDropdownClick = (dropdownName, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  const handleSubDropdownClick = (subDropdownName, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setActiveSubDropdown(activeSubDropdown === subDropdownName ? null : subDropdownName);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setActiveDropdown(null);
      setIsUserDropdownOpen(false);
    }
  };

  const isActivePath = (path) => {
    if (path === '/homeowner') {
      return location.pathname === '/homeowner';
    }
    return location.pathname.startsWith(path);
  };

  const handleNavLinkClick = () => {
    setActiveDropdown(null);
    setIsUserDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleUserDropdownClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsUserDropdownOpen(!isUserDropdownOpen);
    setActiveDropdown(null);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Close all dropdowns when opening/closing the mobile menu
    setActiveDropdown(null);
    setActiveSubDropdown(null);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Add body lock when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add(styles.bodyLock);
    } else {
      document.body.classList.remove(styles.bodyLock);
    }
  }, [isMobileMenuOpen]);

  return (
    <nav className={styles.navbar} ref={dropdownRef}>
      <div className={styles.navbarContainer}>
        <div className={styles.navbarBrand}>
          <Link to="/" onClick={() => handleDropdownClick()}>
            <span className={styles.brandText}>
              {/* <span className={styles.brandTextSmart}>Smart</span>
              <span className={styles.brandTextTech}>Tech</span>
              {' '}
              <span className={styles.brandTextPro}>ProServe</span> */}
              <span className={styles.brandTextTrakvu}>Trakvu</span>
            </span>
          </Link>
          {/* <div className={styles.homeOwnerBadge}>Home Owner</div> */}
        </div>

        {/* Hamburger button for mobile */}
        <button 
          className={`${styles.hamburger} ${isMobileMenuOpen ? styles.active : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        {/* Desktop Navigation */}
        <div className={styles.navbarMenu}>
          <div className={styles.navbarStart}>
          {isLoggedIn && (
          <>
          <Link to="/homeowner/dashboard" className={styles.navItem} onClick={handleNavLinkClick}>
              Dashboard
            </Link>
            </>
            )}  
            <div className={styles.navItem}>
              <div className={`${styles.navItemDropdown} ${activeDropdown === 'solutions' ? styles.active : ''}`}>
                <span onClick={(e) => handleDropdownClick('solutions', e)}>Solutions</span>
                <div className={styles.megaDropdown}>
                  {/* Main Sections */}
                  <div className={styles.mainSections}>
                    {/* Proposal Intelligence */}
                    <div 
                      className={`${styles.mainSection} ${activeDropdown === 'proposal' ? styles.active : ''}`}
                      onClick={(event) => handleDropdownClick('proposal', event)}
                    >
                      <h3>
                        <i className="fas fa-robot"></i>
                        Proposal Intelligence
                      </h3>
                      <div className={styles.subCategories}>
                        <Link to="/upload-quotes" onClick={handleNavLinkClick}>
                          <i className="fas fa-upload"></i>
                          Upload & Compare Proposals
                        </Link>
                        <Link to="/results-preview" onClick={handleNavLinkClick}>
                          <i className="fas fa-brain"></i>
                          AI Proposal Analysis
                        </Link>
                        <Link to="/learn" onClick={handleNavLinkClick}>
                          <i className="fas fa-star"></i>
                          Contractor Ratings
                        </Link>
                        <Link to="/upgrade" onClick={handleNavLinkClick}>
                          <i className="fas fa-award"></i>
                          Best Proposal Recommendation
                        </Link>
                      </div>
                    </div>

                    {/* Project Planning */}
                    <div 
                      className={`${styles.mainSection} ${activeDropdown === 'planning' ? styles.active : ''}`}
                      onClick={(event) => handleDropdownClick('planning', event)}
                    >
                      <h3>
                        <i className="fas fa-folder-open"></i>
                        Project Planning
                      </h3>
                      <div className={styles.subCategories}>
                        <Link to="/my-projects" onClick={handleNavLinkClick}>
                          <i className="fas fa-file-alt"></i>
                          Project Documents
                        </Link>
                        <Link to="/start-project" onClick={handleNavLinkClick}>
                          <i className="fas fa-tasks"></i>
                          Task Checklist Tracker
                        </Link>
                        <Link to="/learn" onClick={handleNavLinkClick}>
                          <i className="fas fa-question-circle"></i>
                          Clarification Questions
                        </Link>
                      </div>
                    </div>

                    {/* Resources */}
                    <div 
                      className={`${styles.mainSection} ${activeDropdown === 'resources' ? styles.active : ''}`}
                      onClick={(event) => handleDropdownClick('resources', event)}
                    >
                      <h3>
                        <i className="fas fa-book"></i>
                        Resources
                      </h3>
                      <div className={styles.subCategories}>
                        <Link to="/learn" onClick={handleNavLinkClick}>
                          <i className="fas fa-graduation-cap"></i>
                          Learning Center
                        </Link>
                        <Link to="/upgrade" onClick={handleNavLinkClick}>
                          <i className="fas fa-gem"></i>
                          Premium Features
                        </Link>
                        <Link to="/support" onClick={handleNavLinkClick}>
                          <i className="fas fa-life-ring"></i>
                          Support & Help
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Link to="/homeowner/pricing" className={styles.navItem} onClick={handleNavLinkClick}>
              Pricing
            </Link>
            <div className={styles.navItem}>
              <div className={`${styles.navItemDropdown} ${activeDropdown === 'support' ? styles.active : ''}`}>
                <span onClick={(e) => handleDropdownClick('support', e)}>Support</span>
                <div className={styles.dropdown}>
                  <div className={styles.supportColumns}>
                    <div className={styles.supportColumn}>
                      <h3 className={styles.supportColumnTitle}>
                        <i className="fas fa-users"></i> Community Support
                      </h3>
                      <Link to="/homeowner/knowledge-base" onClick={handleNavLinkClick}>
                        <i className="fas fa-book"></i>
                        Knowledge Base
                      </Link>
                      <Link to="/homeowner/tutorials" onClick={handleNavLinkClick}>
                        <i className="fas fa-graduation-cap"></i>
                        Tutorials
                      </Link>
                    </div>
                    
                    <div className={styles.supportColumn}>
                      <h3 className={styles.supportColumnTitle}>
                        <i className="fas fa-envelope"></i> Contact Us
                      </h3>
                      <Link to="/homeowner/support" onClick={handleNavLinkClick}>
                        <i className="fas fa-paper-plane"></i>
                        Send Message
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.active : ''}`}>
          <div className={styles.mobileNav}>
            {/* Solutions Dropdown - Mobile */}
            {isLoggedIn && (
            <>
            <Link to="/homeowner/dashboard" className={styles.mobileNavLink} onClick={handleNavLinkClick}>
              Dashboard
            </Link>
            </>
            )}
            <div className={`${styles.mobileNavItem} ${activeDropdown === 'solutions' ? styles.active : ''}`}>
              <div 
                className={styles.mobileNavHeader} 
                onClick={(e) => handleDropdownClick('solutions', e)}
              >
                <span>Solutions</span>
                <i className="fas fa-chevron-down"></i>
              </div>
              <div className={styles.mobileNavContent}>
                {/* Proposal Intelligence - Mobile */}
                <div className={`${styles.mobileSubItem} ${activeSubDropdown === 'proposal' ? styles.active : ''}`}>
                  <div 
                    className={styles.mobileSubHeader}
                    onClick={(e) => handleSubDropdownClick('proposal', e)}
                  >
                    <i className="fas fa-robot"></i>
                    <span>Proposal Intelligence</span>
                    <i className="fas fa-chevron-down"></i>
                  </div>
                  <div className={styles.mobileSubContent}>
                    <Link to="/upload-quotes" onClick={handleNavLinkClick}>
                      <i className="fas fa-upload"></i>
                      Upload & Compare Proposals
                    </Link>
                    <Link to="/results-preview" onClick={handleNavLinkClick}>
                      <i className="fas fa-brain"></i>
                      AI Proposal Analysis
                    </Link>
                    <Link to="/learn" onClick={handleNavLinkClick}>
                      <i className="fas fa-star"></i>
                      Contractor Ratings
                    </Link>
                    <Link to="/upgrade" onClick={handleNavLinkClick}>
                      <i className="fas fa-award"></i>
                      Best Proposal Recommendation
                    </Link>
                  </div>
                </div>

                {/* Project Planning - Mobile */}
                <div className={`${styles.mobileSubItem} ${activeSubDropdown === 'planning' ? styles.active : ''}`}>
                  <div 
                    className={styles.mobileSubHeader}
                    onClick={(e) => handleSubDropdownClick('planning', e)}
                  >
                    <i className="fas fa-folder-open"></i>
                    <span>Project Planning</span>
                    <i className="fas fa-chevron-down"></i>
                  </div>
                  <div className={styles.mobileSubContent}>
                    <Link to="/my-projects" onClick={handleNavLinkClick}>
                      <i className="fas fa-file-alt"></i>
                      Project Documents
                    </Link>
                    <Link to="/start-project" onClick={handleNavLinkClick}>
                      <i className="fas fa-tasks"></i>
                      Task Checklist Tracker
                    </Link>
                    <Link to="/learn" onClick={handleNavLinkClick}>
                      <i className="fas fa-question-circle"></i>
                      Clarification Questions
                    </Link>
                  </div>
                </div>

                {/* Resources - Mobile */}
                <div className={`${styles.mobileSubItem} ${activeSubDropdown === 'resources' ? styles.active : ''}`}>
                  <div 
                    className={styles.mobileSubHeader}
                    onClick={(e) => handleSubDropdownClick('resources', e)}
                  >
                    <i className="fas fa-book"></i>
                    <span>Resources</span>
                    <i className="fas fa-chevron-down"></i>
                  </div>
                  <div className={styles.mobileSubContent}>
                    <Link to="/learn" onClick={handleNavLinkClick}>
                      <i className="fas fa-graduation-cap"></i>
                      Learning Center
                    </Link>
                    <Link to="/upgrade" onClick={handleNavLinkClick}>
                      <i className="fas fa-gem"></i>
                      Premium Features
                    </Link>
                    <Link to="/support" onClick={handleNavLinkClick}>
                      <i className="fas fa-life-ring"></i>
                      Support & Help
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Regular Nav Items - Mobile */}
            <Link to="/homeowner/pricing" className={styles.mobileNavLink} onClick={handleNavLinkClick}>
              Pricing
            </Link>
            

            
            {/* Support Dropdown - Mobile */}
            <div className={`${styles.mobileNavItem} ${activeDropdown === 'mobileSupport' ? styles.active : ''}`}>
              <div 
                className={styles.mobileNavHeader} 
                onClick={(e) => handleDropdownClick('mobileSupport', e)}
              >
                <span>Support</span>
                <i className="fas fa-chevron-down"></i>
              </div>
              <div className={styles.mobileNavContent}>
                {/* Community Support - Mobile */}
                <div className={`${styles.mobileSubItem} ${activeSubDropdown === 'communitySupport' ? styles.active : ''}`}>
                  <div 
                    className={styles.mobileSubHeader}
                    onClick={(e) => handleSubDropdownClick('communitySupport', e)}
                  >
                    <i className="fas fa-users"></i>
                    <span>Community Support</span>
                    <i className="fas fa-chevron-down"></i>
                  </div>
                  <div className={styles.mobileSubContent}>
                    <Link to="/homeowner/knowledge-base" onClick={handleNavLinkClick}>
                      <i className="fas fa-book"></i>
                      Knowledge Base
                    </Link>
                    <Link to="/homeowner/tutorials" onClick={handleNavLinkClick}>
                      <i className="fas fa-graduation-cap"></i>
                      Tutorials
                    </Link>
                  </div>
                </div>

                {/* Contact Us - Mobile */}
                <div className={`${styles.mobileSubItem} ${activeSubDropdown === 'contactUs' ? styles.active : ''}`}>
                  <div 
                    className={styles.mobileSubHeader}
                    onClick={(e) => handleSubDropdownClick('contactUs', e)}
                  >
                    <i className="fas fa-envelope"></i>
                    <span>Contact Us</span>
                    <i className="fas fa-chevron-down"></i>
                  </div>
                  <div className={styles.mobileSubContent}>
                    <Link to="/homeowner/support" onClick={handleNavLinkClick}>
                      <i className="fas fa-paper-plane"></i>
                      Send Message
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Login/Profile Section - Mobile */}
            {isLoggedIn ? (
              <div className={styles.mobileUserInfo}>
                <div className={styles.userAvatar}>
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </div>
                <div>
                  <div className={styles.userName}>{user?.firstName} {user?.lastName}</div>
                  <div className={styles.userEmail}>{user?.email}</div>
                </div>
              </div>
            ) : (
              <div className={styles.mobileLoginContainer}>
                <Link to="/homeowner/login" className={styles.mobileLoginButton} onClick={handleNavLinkClick}>
                  Login
                </Link>
              </div>
            )}
            
            {/* User Profile Links - Mobile */}
            {isLoggedIn && (
              <>
                <Link to="/homeowner/profile" className={styles.mobileNavLink} onClick={handleNavLinkClick}>
                  <i className="fas fa-user"></i>
                  My Profile
                </Link>
                <Link to="/homeowner/billing" className={styles.mobileNavLink} onClick={handleNavLinkClick}>
                  <i className="fas fa-credit-card"></i>
                  Billing
                </Link>
                <Link to="/homeowner/settings" className={styles.mobileNavLink} onClick={handleNavLinkClick}>
                  <i className="fas fa-cog"></i>
                  Settings
                </Link>
                <button 
                  onClick={handleLogout} 
                  className={styles.mobileLogoutButton}
                >
                  <i className="fas fa-sign-out-alt"></i>
                  Logout
                </button>
              </>
            )}
          </div>
        </div>

        <div className={styles.desktopButtons}>
          
          {isLoggedIn ? (
            <div className={styles.userDropdown}>
              <button 
                className={styles.userButton}
                onClick={handleUserDropdownClick}
              >
                <div className={styles.userAvatar}>
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </div>
              </button>

              {isUserDropdownOpen && (
                <div 
                  className={styles.dropdown}
                  style={{ display: 'block' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className={styles.userInfo}>
                    <div className={styles.userName}>
                      {user?.firstName} {user?.lastName}
                    </div>
                    <div className={styles.userEmail}>{user?.email}</div>
                  </div>
                  <div className={styles.dropdownDivider} />
                  <Link 
                    to="/homeowner/profile" 
                    className={styles.dropdownItem}
                    onClick={() => setIsUserDropdownOpen(false)}
                  >
                    <i className="fas fa-user"></i>
                    My Profile
                  </Link>
                  <Link 
                    to="/homeowner/billing" 
                    className={styles.dropdownItem}
                    onClick={() => setIsUserDropdownOpen(false)}
                  >
                    <i className="fas fa-credit-card"></i>
                    Billing
                  </Link>
                  <Link 
                    to="/homeowner/settings" 
                    className={styles.dropdownItem}
                    onClick={() => setIsUserDropdownOpen(false)}
                  >
                    <i className="fas fa-cog"></i>
                    Settings
                  </Link>
                  <div className={styles.dropdownDivider} />
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsUserDropdownOpen(false);
                      handleLogout();
                    }} 
                    className={styles.dropdownItem}
                  >
                    <i className="fas fa-sign-out-alt"></i>
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/homeowner/login" className={styles.loginButton}>
              Login
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};

export default HomeOwnerNavbar; 